"use client";

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { API_BASE_URL } from '@/app/lib/api';
import loggedFetch from '@/lib/logFetch';

// --- Types ---
type Category = {
    id: string;
    name: string;
    slug?: string;
    parentId?: string | null;
    children?: Category[]
};

export interface FilterPayload {
    attributes: Record<string, string[]>;
    categories: string[];
    gammes: string[];
    categorySlugs: string[];
    gammeSlugs: string[];
}

export type AttributeValue = { id: string; value: string };
export type Attribute = { id: string; name: string; values?: AttributeValue[] };

interface Props {
    allCategories?: Category[];
    onAttributesChange?: (filters: FilterPayload) => void;
    // Added to match CategoryView usage
    parentCategoryName?: string;
    groupedSubcategories?: any[];
    activeSlug?: string | null;
    onFilterSelect?: (slug: string | null) => void;
    bgColor?: string;
    attributes?: Attribute[];
}

// --- Icons ---
const IconFilter = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="4" y1="21" y2="14" /><line x1="4" x2="4" y1="10" y2="3" /><line x1="12" x2="12" y1="21" y2="12" /><line x1="12" x2="12" y1="8" y2="3" /><line x1="20" x2="20" y1="21" y2="16" /><line x1="20" x2="20" y1="12" y2="3" /><line x1="1" x2="7" y1="14" y2="14" /><line x1="9" x2="15" y1="8" y2="8" /><line x1="17" x2="23" y1="16" y2="16" /></svg>
);
const IconX = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
);
const IconChevron = ({ isOpen }: { isOpen: boolean }) => (
    <svg className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
);

export default function FilterList({
    allCategories: initialCategories = [],
    onAttributesChange
}: Props) {
    const [allCategories, setAllCategories] = useState<Category[]>(initialCategories);
    const [isLoading, setIsLoading] = useState(!initialCategories.length);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [openParents, setOpenParents] = useState<string[]>([]);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const isFirstRender = useRef(true);

    // --- Fetching Data ---
    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            if (initialCategories.length > 0) return;
            setIsLoading(true);
            try {
                const baseUrl = API_BASE_URL.replace(/\/$/, '');
                const catRes = await loggedFetch(`${baseUrl}/categories`, { cache: 'no-store' });
                if (mounted && catRes.ok) {
                    const data = await catRes.json();
                    const list = Array.isArray(data) ? data : (data.data || data.items || []);
                    setAllCategories(list);
                }
            } catch (error) {
                console.error("❌ FilterList error:", error);
            } finally {
                if (mounted) setIsLoading(false);
            }
        };
        fetchData();
        return () => { mounted = false; };
    }, [initialCategories.length]);

    // Construction de l'arbre des catégories
    const categoryTree = useMemo(() => {
        const excludedSlugs = new Set(['meilleures-ventes', 'nos-coups-de-coeur']);
        const roots = allCategories.filter(c => {
            const pid = c.parentId === null || c.parentId === undefined ? '' : String(c.parentId);
            const isRoot = !pid || pid === '0';
            return isRoot && !excludedSlugs.has(c.slug || '');
        });

        return roots.map(parent => ({
            parent,
            children: allCategories.filter(c =>
                String(c.parentId) === String(parent.id) && !excludedSlugs.has(c.slug || '')
            )
        }));
    }, [allCategories]);

    // Ouvrir les parents par défaut
    useEffect(() => {
        if (categoryTree.length > 0 && openParents.length === 0) {
            setOpenParents(categoryTree.map(t => String(t.parent.id)));
        }
    }, [categoryTree]);

    // --- Émission des changements ---
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const payload: FilterPayload = {
            attributes: {},
            categories: selectedCategories,
            gammes: [],
            categorySlugs: allCategories
                .filter(c => selectedCategories.includes(String(c.id)))
                .map(c => c.slug || ''),
            gammeSlugs: [],
        };
        onAttributesChange?.(payload);
        window.dispatchEvent(new CustomEvent('filters-changed', { detail: payload }));
    }, [selectedCategories, allCategories, onAttributesChange]);

    const toggleCategory = (id: string) => {
        setSelectedCategories(prev =>
            prev.includes(id) ? [] : [id]
        );
    };

    const toggleParentOpen = (id: string) => {
        setOpenParents(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const activeCount = selectedCategories.length;

    // --- Composant Interne: Contenu des Filtres ---
    const FiltersContent = () => (
        <div className="flex flex-col h-full space-y-6">
            

            <div className="space-y-1">
 
                {categoryTree.length === 0 && !isLoading && (
                    <p className="text-sm text-white/40 italic">Aucune catégorie disponible</p>
                )}

                <div className="space-y-2">
                    {categoryTree.map(({ parent, children }) => {
                        const isOpen = openParents.includes(String(parent.id));
                        const isSelected = selectedCategories.includes(String(parent.id));

                        return (
                            <div key={parent.id} className="group">
                                {/* Parent Row */}
                                <div className={`flex items-center group/row rounded-lg transition-all ${isSelected ? 'bg-white/10' : 'hover:bg-white/5'}`}>
                                    <label className="flex flex-1 items-center gap-3 p-1 cursor-pointer">
                                        <div className="relative flex items-center">
                                            <input
                                                type="radio"
                                                checked={isSelected}
                                                onChange={() => toggleCategory(String(parent.id))}
                                                className="peer appearance-none h-5 w-5 border-2 border-white/20 rounded-full checked:bg-[#ab1e8f] checked:border-[#ab1e8f] transition-all cursor-pointer"
                                            />
                                            <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 left-[3px] pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                        </div>
                                        <span className={`text-lg font-semibold transition-colors ${isSelected ? 'text-white' : 'text-white/70 group-hover/row:text-white'}`}>
                                            {parent.name}
                                        </span>
                                    </label>

                                    {children.length > 0 && (
                                        <button
                                            onClick={() => toggleParentOpen(String(parent.id))}
                                            className="p-2.5 text-white/30 hover:text-white transition-colors"
                                        >
                                            <IconChevron isOpen={isOpen} />
                                        </button>
                                    )}
                                </div>

                                {/* Children List */}
                                {isOpen && children.length > 0 && (
                                    <div className="ml-7 mt-1 space-y-1 border-l border-white/10 pl-2">
                                        {children.map(child => {
                                            const isChildSelected = selectedCategories.includes(String(child.id));
                                            return (
                                                <label key={child.id} className={`flex items-center gap-3 p-1 rounded-md cursor-pointer transition-colors ${isChildSelected ? 'bg-[#ab1e8f]/10 text-white' : 'text-white hover:text-white hover:bg-white/5'}`}>
                                                    <div className="relative flex items-center">
                                                        <input
                                                            type="radio"
                                                            checked={isChildSelected}
                                                            onChange={() => toggleCategory(String(child.id))}
                                                            className="peer appearance-none h-4 w-4 border-2 border-white/20 rounded-full checked:bg-[#ab1e8f] checked:border-[#FFFFFF] transition-all cursor-pointer"
                                                        />
                                                        <svg className="absolute w-2.5 h-2.5 text-white opacity-0 peer-checked:opacity-100 left-[3px] pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                    </div>
                                                    <span className="text-lg">{child.name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <style jsx global>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: white; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: white; }
            `}</style>

            {/* Desktop Sidebar */}
            <div className="hidden lg:block w-full">
                <div className="sticky top-28 bg-main backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl max-h-[calc(100vh-150px)] overflow-y-auto custom-scrollbar">
                    {isLoading ? (
                        <div className="animate-pulse space-y-6">
                            <div className="h-4 bg-white/10 rounded w-1/2"></div>
                            <div className="space-y-3">
                                {[1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-white/5 rounded-lg"></div>)}
                            </div>
                        </div>
                    ) : <FiltersContent />}
                </div>
            </div>

            {/* Mobile Trigger */}
            <div className="lg:hidden">
                <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-40 transition-transform duration-500 ${isMobileOpen ? 'translate-y-32' : 'translate-y-0'}`}>
                    <button
                        onClick={() => setIsMobileOpen(true)}
                        className="flex items-center gap-3 bg-white text-black px-8 py-4 rounded-full shadow-[0_20px_50px_rgba(0,0,0,0.3)] hover:scale-105 active:scale-95 transition-all"
                    >
                        <IconFilter />
                        <span className="font-bold text-sm tracking-wide uppercase">Filtrer</span>
                        {activeCount > 0 && (
                            <span className="bg-[#ab1e8f] text-white text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full">
                                {activeCount}
                            </span>
                        )}
                    </button>
                </div>

                {/* Mobile Drawer */}
                <div className={`fixed inset-0 z-50 transition-visibility ${isMobileOpen ? 'visible' : 'invisible'}`}>
                    <div
                        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${isMobileOpen ? 'opacity-100' : 'opacity-0'}`}
                        onClick={() => setIsMobileOpen(false)}
                    />

                    <div className={`absolute inset-y-0 right-0 w-[85%] max-w-sm bg-main shadow-2xl transform transition-transform duration-500 ease-out flex flex-col ${isMobileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                        <div className="flex items-center justify-between p-6 border-b border-white/5">
                            <span className="text-lg font-bold text-white">Filtres</span>
                            <button onClick={() => setIsMobileOpen(false)} className="p-2 text-white hover:text-white"><IconX /></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                            {isLoading ? (
                                <div className="animate-pulse space-y-4"><div className="h-8 bg-white/5 rounded w-1/2"></div><div className="h-64 bg-white/5 rounded-xl"></div></div>
                            ) : <FiltersContent />}
                        </div>

                        <div className="p-6 bg-main border-t border-white/5">
                            <button
                                onClick={() => setIsMobileOpen(false)}
                                className="w-full py-4 bg-gray-500 text-white rounded-xl font-bold text-sm hover:brightness-110 transition-all shadow-lg shadow-[#ab1e8f]/20"
                            >
                                Afficher les résultats
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}